'use client'

import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, postTypeLabel } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { RawEntry } from './trustCenterTypes'
import { parseDbTimestamp } from '@/lib/dates'

export function TrustCenterProcessedCard({
  entry,
  theme,
  onClick,
  isSelected,
}: {
  entry: RawEntry
  theme: ProcessedTheme
  onClick?: () => void
  isSelected?: boolean
}) {
  const c = watchClasses(theme)
  const title = entry.headline ?? entry.message_content?.slice(0, 80) ?? '—'
  const dateLabel = compactDate(entry.created_at)
  const dayMonth = (() => {
    const d = parseDbTimestamp(entry.created_at)
    if (!d) return '—'
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  })()
  const dark = theme === 'dark'

  return (
    <article
      className={`border-b transition-colors ${c.line} ${onClick ? 'cursor-pointer' : ''} ${isSelected ? (dark ? 'bg-signal-surface border-l-2 border-l-signal' : 'bg-signal-surface border-l-2 border-l-signal') : onClick ? (dark ? 'hover:bg-[#121a21]' : 'hover:bg-secondary') : ''}`}
      onClick={onClick}
    >
      <div className="grid w-full grid-cols-[1fr_auto] gap-6 px-7 py-6">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${c.line} ${c.soft}`}>
              <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Entry</span>
              <span className={`font-mono text-[10px] ${c.muted}`}>{entry.id.slice(0, 8)}…</span>
            </span>
            <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${dark ? 'bg-warning-surface border-warning-border text-warning' : 'bg-warning-surface border-warning-border text-warning'}`}>Processed</span>
            <span className={`text-xs ${c.muted}`}>first seen {dateLabel}</span>
          </div>
          <h2 className={`mt-4 text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
            "{title}"
          </h2>
          {entry.message_content && entry.headline && (
            <p className={`mt-3 max-w-4xl text-[17px] leading-7 ${c.muted}`}>
              {entry.message_content}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {entry.post_type && (
              <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${c.line} ${c.soft}`}>
                {postTypeLabel(entry.post_type)}{entry.sender ? ` · ${entry.sender}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="hidden min-w-[120px] text-right md:block">
          <div className={`font-mono text-[34px] leading-none tracking-tight ${c.text}`}>{dayMonth}</div>
          <div className={`mt-2 font-mono text-[10px] uppercase tracking-[0.3em] ${c.label}`}>submitted</div>
        </div>
      </div>
    </article>
  )
}
